import { demoRoutes, setDemoTourResume } from '../DemoFlow'
import demoStore from '../mock/DemoStore'
import { DEMO_IDS } from '../mock/fixtures/mqttPacket'
import demoSession from '../DemoSession'
import { skipStageConfig } from '../demoSkipStage'
import { persistIsTourConcluido } from '../../primeiroLogin'
import { selectFirstReactSelectOption } from '../../shared/domHelpers'

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

export const DEMO_MONITORADO_DEMO_LISTA_INDEX = 0
export const DEMO_MONITORADO_DEMO_STATUS_INDEX = 3
export const DEMO_MONITORADO_DEMO_SUPERVISORIO_INDEX = 4
export const DEMO_MONITORADO_DEMO_CONCLUIDA_INDEX = 5

export const ensureMonitoradoDemoData = () => {
    demoStore.seedThroughStage('monitorado-demo')
}

const getMonitoradoNomeLink = () =>
    document.querySelector('[data-tour="lista-monitorados-nome"]') ||
    document.querySelector('[data-tour="lista-monitorados-tabela"] tbody tr td a')

const pathMatches = (targetPath) => {
    const current = window.location.pathname
    const target = String(targetPath || '/').split('?')[0]
    if (target === '/') return current === '/'
    return current === target || current.startsWith(`${target}/`)
}

const goToMonitoradoDemoStep = (tourManager, path, resumeIndex) => {
    ensureMonitoradoDemoData()

    if (tourManager.isActive()) {
        tourManager.destroy()
    }

    const startStep = () => {
        tourManager.start('demo-monitorado-demo', { resumeIndex })
    }

    if (tourManager.history) {
        if (!pathMatches(path)) {
            tourManager.history.push(path)
            setTimeout(startStep, 400)
            return
        }
        setTimeout(startStep, 120)
        return
    }

    setDemoTourResume('demo-monitorado-demo', path, resumeIndex)
    window.location.href = path
}

const goToStatus = (tourManager) => {
    goToMonitoradoDemoStep(tourManager, demoRoutes.status, DEMO_MONITORADO_DEMO_STATUS_INDEX)
}

const bindStatusLink = (element, tourManager) => {
    const link =
        element?.matches?.('[data-tour="lista-monitorados-nome"], a')
            ? element.closest?.('a') || element
            : element?.querySelector?.('[data-tour="lista-monitorados-nome"], a') ||
              getMonitoradoNomeLink()

    if (!link || link._hdvDemoStatusClick) return

    if (link.tagName === 'A') link.setAttribute('href', demoRoutes.status)

    const onClick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
        goToStatus(tourManager)
    }

    link.addEventListener('click', onClick, true)
    link._hdvDemoStatusClick = onClick
}

const unbindStatusLink = (element) => {
    const link =
        element?.closest?.('a') ||
        element?.querySelector?.('[data-tour="lista-monitorados-nome"], a') ||
        getMonitoradoNomeLink()

    if (!link?._hdvDemoStatusClick) return
    link.removeEventListener('click', link._hdvDemoStatusClick, true)
    delete link._hdvDemoStatusClick
}

const bindListaMonitoradoLinks = (tourManager) => {
    const links = document.querySelectorAll(
        '[data-tour="lista-monitorados-nome"], [data-tour="lista-monitorados-tabela"] tbody tr td a'
    )
    links.forEach((link) => bindStatusLink(link, tourManager))
}

const unbindListaMonitoradoLinks = () => {
    const links = document.querySelectorAll(
        '[data-tour="lista-monitorados-nome"], [data-tour="lista-monitorados-tabela"] tbody tr td a'
    )
    links.forEach((link) => {
        if (!link._hdvDemoStatusClick) return
        link.removeEventListener('click', link._hdvDemoStatusClick, true)
        delete link._hdvDemoStatusClick
    })
}

const ensureListaMonitoradosReady = async (tourManager) => {
    ensureMonitoradoDemoData()

    const listaVisible = document.querySelector(
        '[data-tour="lista-monitorados-tabela"], [data-tour="help-lista-monitorados"]'
    )
    if (!listaVisible && tourManager.history) {
        tourManager.history.push(demoRoutes.listaMonitorados)
        await delay(400)
    }

    document.querySelector('[data-tour="lista-monitorados-tabela"] .la-redo-alt')?.closest('button')?.click()
    await delay(250)

    try {
        await tourManager.waitForElement(getMonitoradoNomeLink, 4000)
    } catch (error) {}

    bindListaMonitoradoLinks(tourManager)
}

const ensureSupervisorioSelecionado = async (tourManager) => {
    ensureMonitoradoDemoData()

    if (!pathMatches(demoRoutes.supervisorio)) {
        if (tourManager.history) {
            tourManager.history.push(demoRoutes.supervisorio)
            await delay(400)
        } else {
            window.location.href = demoRoutes.supervisorio
            return
        }
    }

    try {
        await tourManager.waitForElement('.select-supervisory, .hdv-barsuper', 6000)
    } catch (error) {}

    await delay(250)

    const alreadySelected = document.querySelector(
        '.select-supervisory .rs__single-value, .hdv-sup-title'
    )
    const selectedText = (alreadySelected?.textContent || '').trim()
    if (/supervis[oó]rio demonstra/i.test(selectedText)) {
        return
    }

    const selectedFromMenu = await new Promise((resolve) => {
        const container = document.querySelector('.select-supervisory')
        if (!container) {
            resolve(false)
            return
        }

        const control = container.querySelector('.rs__control')
        if (!control) {
            resolve(false)
            return
        }

        control.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))

        setTimeout(() => {
            const options = Array.from(document.querySelectorAll('.rs__option'))
            const demoOption =
                options.find((option) =>
                    /supervis[oó]rio demonstra/i.test((option.textContent || '').trim())
                ) || options[0]

            if (demoOption) {
                demoOption.click()
                resolve(true)
                return
            }

            resolve(false)
        }, 180)
    })

    if (!selectedFromMenu) {
        await selectFirstReactSelectOption('.select-supervisory')
    }

    await delay(400)
}

const finalizarGuiaMantendoSandbox = (tourManager) => {
    persistIsTourConcluido(true)
        .catch(() => {})
        .finally(() => {
            demoSession.markGuideDone()
            if (tourManager.isActive()) tourManager.finish('demo-monitorado-demo')
        })
}

const buildDemoMonitoradoDemoTourSteps = (tourManager) => [
    {
        ensureReady: async () => {
            await ensureListaMonitoradosReady(tourManager)
        },
        onHighlighted: () => bindListaMonitoradoLinks(tourManager),
        onDeselected: () => unbindListaMonitoradoLinks(),
        skipStage: skipStageConfig('monitoradoDemoConcluida'),
        popover: {
            title: 'Lista de Monitorados',
            description:
                'Aqui ficam os monitorados cadastrados. É nesta tela que você acompanha as leituras e a situação de cada ponto monitorado.<br><br>' +
                'Clique em <strong>Pular etapa</strong> para finalizar o guia e ir direto ao modo livre da demonstração.',
            side: 'over',
            align: 'center',
            onNextClick: () => tourManager.moveNext(),
        },
    },
    {
        element: '[data-tour="lista-monitorados-tabela"]',
        ensureReady: async () => {
            await ensureListaMonitoradosReady(tourManager)
            await tourManager.waitForElement(
                '[data-tour="lista-monitorados-tabela"], [data-tour="help-lista-monitorados"]'
            )
        },
        onHighlighted: () => bindListaMonitoradoLinks(tourManager),
        onDeselected: () => unbindListaMonitoradoLinks(),
        popover: {
            title: 'Seus monitorados',
            description:
                'A tabela mostra nome, cliente, tipo e a última comunicação de cada monitorado. Clique no nome do monitorado ou em <strong>Próximo</strong> para continuar.',
            side: 'top',
            align: 'start',
            onNextClick: () => tourManager.moveNext(),
        },
    },
    {
        element: getMonitoradoNomeLink,
        ensureReady: async () => {
            await ensureListaMonitoradosReady(tourManager)
            const link = getMonitoradoNomeLink()
            if (link?.tagName === 'A') link.setAttribute('href', demoRoutes.status)
        },
        onHighlighted: (element) => bindStatusLink(element, tourManager),
        onDeselected: (element) => unbindStatusLink(element),
        popover: {
            title: 'Abrir status',
            description:
                'Clique no <strong>nome</strong> do monitorado para abrir a tela de Status. Lá você vê gráficos e os dados de leitura dos parâmetros do equipamento vinculado a esse monitorado.<br><br>Na demonstração, clique no nome ou em <strong>Abrir status</strong> para continuar.',
            side: 'bottom',
            align: 'start',
            nextBtnText: 'Abrir status',
            onNextClick: () => goToStatus(tourManager),
        },
    },
    {
        ensureReady: async () => {
            ensureMonitoradoDemoData()
            if (!window.location.pathname.startsWith(`/status/${DEMO_IDS.monitorado}`)) {
                if (tourManager.history) tourManager.history.push(demoRoutes.status)
                await delay(250)
            }
            await delay(150)
        },
        popover: {
            title: 'Status e gráficos',
            description:
                'Nesta tela você acompanha valores de leituras atuais e o histórico dos parâmetros vinculados.',
            side: 'over',
            align: 'center',
            onNextClick: () =>
                goToMonitoradoDemoStep(
                    tourManager,
                    demoRoutes.supervisorio,
                    DEMO_MONITORADO_DEMO_SUPERVISORIO_INDEX
                ),
        },
    },
    {
        ensureReady: async () => {
            await ensureSupervisorioSelecionado(tourManager)
        },
        popover: {
            title: 'Supervisório demonstração',
            description:
                'Nesta tela você monta um painel visual com widgets para acompanhar, em tempo real, os parâmetros dos monitorados.',
            side: 'over',
            align: 'center',
            nextBtnText: 'Concluir demonstração',
            onNextClick: () =>
                goToMonitoradoDemoStep(tourManager, '/', DEMO_MONITORADO_DEMO_CONCLUIDA_INDEX),
        },
    },
    {
        ensureReady: async () => {
            if (window.location.pathname !== '/') {
                if (tourManager.history) tourManager.history.push('/')
                await delay(300)
            }
        },
        onHighlighted: () => {
            setTimeout(() => {
                const exitLink = document.getElementById('hdv-demo-exit-from-tour')
                if (!exitLink || exitLink._hdvBound) return
                exitLink._hdvBound = true
                exitLink.addEventListener('click', (event) => {
                    event.preventDefault()
                    demoSession.stop()
                })
            }, 0)
        },
        popover: {
            title: 'Demonstração concluída',
            description:
                'Você percorreu o fluxo completo.<br><br>' +
                'Clique em <strong>Finalizar</strong> para encerrar o guia e continuar explorando livremente as telas com os dados de demonstração.<br><br>' +
                'Quando quiser voltar ao sistema real, use o botão <a href="#" id="hdv-demo-exit-from-tour">sair do modo demonstração</a>. ',
            nextBtnText: 'Finalizar',
            doneBtnText: 'Finalizar',
            showButtons: ['previous', 'next'],
            onNextClick: () => finalizarGuiaMantendoSandbox(tourManager),
            onDoneClick: () => finalizarGuiaMantendoSandbox(tourManager),
        },
    },
]

export default buildDemoMonitoradoDemoTourSteps
